/**
 * PDF/image preview for an inbound document (PF-136).
 *
 * Uses the same signed download URL as AttachmentsCard, shown in a ResponsiveDialog.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import {
  useInboundAttachmentDownload,
  type InboundDocument,
} from '@/hooks/useInboundDocuments';

interface InboundDocumentPreviewDialogProps {
  open: boolean;
  document: InboundDocument | null;
  onClose: () => void;
}

function previewKind(fileName: string): 'image' | 'pdf' | 'other' {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.pdf')) return 'pdf';
  if (/\.(png|jpe?g|webp|gif|heic)$/.test(lower)) return 'image';
  return 'other';
}

export function InboundDocumentPreviewDialog({
  open,
  document,
  onClose,
}: InboundDocumentPreviewDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const attachmentIds = document?.attachmentIds ?? [];
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const activeId = selectedId ?? attachmentIds[0];
  const preview = useInboundAttachmentDownload(open ? activeId : undefined);

  const kind = useMemo(
    () => (preview.data ? previewKind(preview.data.fileName) : 'other'),
    [preview.data],
  );

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      mobileTitle={t('inbox.preview')}
    >
      <DialogTitle>{t('inbox.preview')}</DialogTitle>
      <DialogContent>
        {attachmentIds.length > 1 && (
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
            {attachmentIds.map((id, index) => (
              <Chip
                key={id}
                size="small"
                color={id === activeId ? 'primary' : 'default'}
                label={t('inbox.attachmentIndex', { index: index + 1 })}
                onClick={() => setSelectedId(id)}
              />
            ))}
          </Stack>
        )}

        {attachmentIds.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('inbox.noPreview')}
          </Typography>
        )}

        {attachmentIds.length > 0 && preview.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {preview.data && kind === 'image' && (
          <Box
            component="img"
            src={preview.data.downloadUrl}
            alt={preview.data.fileName}
            sx={{
              maxWidth: '100%',
              maxHeight: 480,
              display: 'block',
              mx: 'auto',
              borderRadius: 1,
              objectFit: 'contain',
            }}
          />
        )}

        {preview.data && kind === 'pdf' && (
          <Box
            component="iframe"
            title={preview.data.fileName}
            src={preview.data.downloadUrl}
            sx={{ width: '100%', height: 480, border: 0 }}
          />
        )}

        {preview.data && kind === 'other' && (
          <Stack alignItems="center" spacing={1} sx={{ py: 4, color: 'action.active' }}>
            <InsertDriveFileIcon sx={{ fontSize: 64 }} />
            <Typography variant="body2">{preview.data.fileName}</Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tc('close')}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
